import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import fontNotoSansUrl from '../../assets/fonts/NotoSans-Regular.ttf?url';

import { dungApiThat, goiApi } from '../api/http';
import { BAO_CAO_RONG, MOCK_BAO_CAO } from '../../mock/bao-cao';
import { mockRequest } from '../../mock/transport';
import type { BaoCaoTho, BarEntry, BaoCaoTongHop } from '../../modules/bao-cao/types';
import type { Chatbot } from '../../modules/tro-ly-ai/types';
import { documentRepository } from './document.repository';
import { loaiChungTuRepository } from './ocr-schema.repository';
import { phongBanRepository } from './phong-ban.repository';
import { nguoiDungRepository } from './nguoi-dung.repository';
import { triThucRepository } from './tri-thuc.repository';
import { vaiTroRepository } from './vai-tro.repository';

const TRANG = 100;

/** Đọc hết các trang (cursor); các trang sau tuần tự vì cursor phụ thuộc trang trước. */
async function docHet<T>(
  trang: (cursor?: string) => Promise<{ items: T[]; next_cursor: string | null }>,
): Promise<T[]> {
  const tatCa: T[] = [];
  let cursor: string | undefined;
  do {
    const t = await trang(cursor);
    tatCa.push(...t.items);
    cursor = t.next_cursor ?? undefined;
  } while (cursor);
  return tatCa;
}

/**
 * Gọi song song, **chịu lỗi từng phần**: yêu cầu nào hỏng thì chỉ phần đó rỗng,
 * `soLoi` tăng để view báo; các phần còn lại vẫn hiển thị.
 */
async function taiThat(): Promise<BaoCaoTho> {
  const [ct, schema, khoStats, khoList, bots, users, roles, phong, docs] = await Promise.allSettled([
    documentRepository.thongKe(),
    loaiChungTuRepository.thongKe(),
    triThucRepository.thongKe(),
    docHet((cursor) => triThucRepository.danhSachKho({ limit: TRANG, cursor })),
    goiApi<Chatbot[]>('/api/v1/m/tro-ly-ai/chatbots', { query: { quan_tri: 'true' } }),
    docHet((cursor) => nguoiDungRepository.danhSach({ limit: TRANG, cursor })),
    docHet((cursor) => vaiTroRepository.danhSach({ limit: TRANG, cursor })),
    docHet((cursor) => phongBanRepository.danhSach({ limit: TRANG, cursor })),
    docHet((cursor) => documentRepository.danhSach({ limit: TRANG, cursor })),
  ]);
  const ok = <T>(r: PromiseSettledResult<T>): T | null => (r.status === 'fulfilled' ? r.value : null);
  const soLoi = [ct, schema, khoStats, khoList, bots, users, roles, phong, docs].filter(
    (r) => r.status === 'rejected',
  ).length;

  const c = ok(ct);
  const k = ok(khoStats);
  return {
    chungTu: c && { total: c.total, confirmed: c.confirmed, transferred: c.transferred },
    soMauOcr: ok(schema)?.active_schemas ?? null,
    loaiChungTu: (ok(docs) ?? []).map((d) => d.schema?.type ?? 'OTHERS'),
    khoStats: k && {
      tongBo: k.total_knowledge_bases,
      tongTep: k.total_files,
      phongBan: k.departments_using_count,
    },
    khoList: (ok(khoList) ?? []).map((b) => ({
      name: b.name,
      totalFiles: b.file_counts?.total ?? 0,
      pdf: b.file_counts?.pdf ?? 0,
    })),
    chatbots: (ok(bots) ?? []).map((b) => ({
      active: b.active,
      answerable: b.answerable,
      mode: b.mode,
      scenariosEnabled: b.scenariosEnabled,
    })),
    nguoiDung: (ok(users) ?? []).map((u) => ({ departmentId: u.department_id, active: u.status === 'ACTIVE' })),
    soVaiTro: ok(roles)?.length ?? 0,
    phongBan: (ok(phong) ?? []).map((p) => ({ id: p.id, name: p.name })),
    soLoi,
  };
}

export const baoCaoRepository = {
  tai: (): Promise<BaoCaoTho> => (dungApiThat() ? taiThat() : mockRequest(MOCK_BAO_CAO, BAO_CAO_RONG)),
};

// ───────────────────────── Xuất báo cáo ─────────────────────────

/** Cột chung cho bảng tổng quan (Excel và PDF). */
interface DongTongQuan {
  nhom: string;
  chiSo: string;
  giaTri: number;
}

/** Hàm dịch của i18n: nhận khoá, trả chuỗi. */
type TDich = (khoa: string) => string;

function dongTongQuan(tong: BaoCaoTongHop, t: TDich): DongTongQuan[] {
  return [
    { nhom: t('baoCao.xuatNhomOcr'), chiSo: t('baoCao.mauOcr'), giaTri: tong.ocr.mauOcr },
    { nhom: t('baoCao.xuatNhomOcr'), chiSo: t('baoCao.tongChungTu'), giaTri: tong.ocr.tongChungTu },
    { nhom: t('baoCao.xuatNhomOcr'), chiSo: t('baoCao.daXacNhan'), giaTri: tong.ocr.daXacNhan },
    { nhom: t('baoCao.xuatNhomOcr'), chiSo: t('baoCao.vaoKho'), giaTri: tong.ocr.vaoKho },
    { nhom: t('baoCao.xuatNhomKho'), chiSo: t('baoCao.boTriThuc'), giaTri: tong.kho.boTriThuc },
    { nhom: t('baoCao.xuatNhomKho'), chiSo: t('baoCao.tongTep'), giaTri: tong.kho.tongTep },
    { nhom: t('baoCao.xuatNhomKho'), chiSo: t('baoCao.phongBan'), giaTri: tong.kho.phongBan },
    { nhom: t('baoCao.xuatNhomKho'), chiSo: t('baoCao.tepPdf'), giaTri: tong.kho.tepPdf },
    { nhom: t('baoCao.xuatNhomBot'), chiSo: t('baoCao.tongBot'), giaTri: tong.chatbot.tong },
    { nhom: t('baoCao.xuatNhomBot'), chiSo: t('baoCao.botHoatDong'), giaTri: tong.chatbot.hoatDong },
    { nhom: t('baoCao.xuatNhomBot'), chiSo: t('baoCao.coKichBan'), giaTri: tong.chatbot.coKichBan },
    { nhom: t('baoCao.xuatNhomBot'), chiSo: t('baoCao.traLoiDuoc'), giaTri: tong.chatbot.traLoiDuoc },
    { nhom: t('baoCao.xuatNhomHeThong'), chiSo: t('baoCao.nguoiDung'), giaTri: tong.heThong.nguoiDung },
    { nhom: t('baoCao.xuatNhomHeThong'), chiSo: t('baoCao.nguoiDungHoatDong'), giaTri: tong.heThong.hoatDong },
    { nhom: t('baoCao.xuatNhomHeThong'), chiSo: t('baoCao.vaiTro'), giaTri: tong.heThong.vaiTro },
    { nhom: t('baoCao.xuatNhomHeThong'), chiSo: t('baoCao.mauOcr'), giaTri: tong.heThong.mauOcr },
  ];
}

const FONT_PDF = 'NotoSans';
const LE_TRAI = 14;
let fontBase64: Promise<string> | null = null;

/** Helvetica mặc định của jsPDF không có glyph tiếng Việt, nên nhúng NotoSans (TTF) vào mỗi tệp PDF. */
function taiFont(): Promise<string> {
  fontBase64 ??= fetch(fontNotoSansUrl)
    .then((r) => {
      if (!r.ok) throw new Error('font');
      return r.arrayBuffer();
    })
    .then((buf) => {
      let nhiPhan = '';
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) {
        nhiPhan += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      }
      return btoa(nhiPhan);
    })
    .catch((e) => {
      fontBase64 = null; // lần sau cho thử lại
      throw e;
    });
  return fontBase64;
}

function lastY(doc: jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
}

/** Nếu còn ít chỗ thì sang trang mới, tránh tiêu đề mồ côi cuối trang. */
function giuCho(doc: jsPDF, y: number, canMm: number): number {
  if (y + canMm <= doc.internal.pageSize.getHeight() - 15) return y;
  doc.addPage();
  return 20;
}

function bangTheoThanh(doc: jsPDF, tieuDe: string, muc: BarEntry[], batDauY: number, t: TDich): number {
  const y = giuCho(doc, batDauY, 30);
  doc.setFontSize(12);
  doc.text(tieuDe, LE_TRAI, y);
  autoTable(doc, {
    startY: y + 4,
    head: [[t('baoCao.xuatTen'), t('baoCao.xuatSoLuong')]],
    body: muc.map((m) => [m.nhan, String(m.value)]),
    margin: { left: LE_TRAI, right: LE_TRAI },
    styles: { font: FONT_PDF, fontSize: 9 },
    headStyles: { font: FONT_PDF, fontStyle: 'normal', fillColor: [79, 70, 229] },
    columnStyles: { 1: { halign: 'right', cellWidth: 30 } },
  });
  return lastY(doc) + 10;
}

/**
 * Sinh workbook Excel 5 sheet từ dữ liệu đã tổng hợp.
 * Trả về `Blob` để `luuBlob` tải về.
 */
export function xuatExcel(tong: BaoCaoTongHop, tieuDe: string, ngay: string, t: TDich): Blob {
  const wb = XLSX.utils.book_new();

  // Sheet 1 — Tổng quan
  const dongTq = dongTongQuan(tong, t);
  const wsTong = XLSX.utils.aoa_to_sheet([
    [tieuDe],
    [ngay],
    [],
    [t('baoCao.xuatNhom'), t('baoCao.xuatChiSo'), t('baoCao.xuatGiaTri')],
    ...dongTq.map((d) => [d.nhom, d.chiSo, d.giaTri]),
  ]);
  wsTong['!cols'] = [{ wch: 18 }, { wch: 28 }, { wch: 14 }];
  wsTong['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
  ];
  XLSX.utils.book_append_sheet(wb, wsTong, t('baoCao.xuatTongQuan'));

  // Sheet 2–5 — Chi tiết theo panel
  const sheets: Array<[string, BarEntry[]]> = [
    [t('baoCao.xuatSheetOcr'), tong.ocr.theoLoai],
    [t('baoCao.xuatSheetKho'), tong.kho.theoBo],
    [t('baoCao.xuatSheetBot'), tong.chatbot.theoPhamVi],
    [t('baoCao.xuatSheetHeThong'), tong.heThong.theoPhongBan],
  ];
  for (const [ten, muc] of sheets) {
    const ws = XLSX.utils.aoa_to_sheet([
      [ten],
      [],
      [t('baoCao.xuatTen'), t('baoCao.xuatSoLuong')],
      ...muc.map((m) => [m.nhan, m.value]),
    ]);
    ws['!cols'] = [{ wch: 40 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws, ten.slice(0, 31)); // Tên sheet tối đa 31 ký tự
  }

  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

/**
 * Sinh file PDF từ dữ liệu đã tổng hợp (bất đồng bộ vì phải nạp font Unicode).
 * Trả về `Blob` để `luuBlob` tải về.
 */
export async function xuatPdf(tong: BaoCaoTongHop, tieuDe: string, ngay: string, t: TDich): Promise<Blob> {
  const font = await taiFont();
  const doc = new jsPDF('p', 'mm', 'a4');
  doc.addFileToVFS('NotoSans-Regular.ttf', font);
  doc.addFont('NotoSans-Regular.ttf', FONT_PDF, 'normal');
  doc.setFont(FONT_PDF, 'normal');
  const rong = doc.internal.pageSize.getWidth();

  // Đầu trang: tiêu đề tự xuống dòng nếu dài
  doc.setFontSize(18);
  const dongTieuDe = doc.splitTextToSize(tieuDe, rong - 2 * LE_TRAI) as string[];
  doc.text(dongTieuDe, LE_TRAI, 22);
  const yNgay = 22 + dongTieuDe.length * 8;
  doc.setFontSize(10);
  doc.text(ngay, LE_TRAI, yNgay);

  // Bảng tổng quan
  doc.setFontSize(13);
  doc.text(t('baoCao.xuatTongQuan'), LE_TRAI, yNgay + 12);
  autoTable(doc, {
    startY: yNgay + 16,
    head: [[t('baoCao.xuatNhom'), t('baoCao.xuatChiSo'), t('baoCao.xuatGiaTri')]],
    body: dongTongQuan(tong, t).map((d) => [d.nhom, d.chiSo, String(d.giaTri)]),
    margin: { left: LE_TRAI, right: LE_TRAI },
    styles: { font: FONT_PDF, fontSize: 9 },
    headStyles: { font: FONT_PDF, fontStyle: 'normal', fillColor: [79, 70, 229] },
    columnStyles: { 2: { halign: 'right', cellWidth: 30 } },
  });

  // Các bảng chi tiết
  let y = giuCho(doc, lastY(doc) + 12, 40);
  doc.setFontSize(13);
  doc.text(t('baoCao.xuatChiTiet'), LE_TRAI, y);
  y += 8;
  const bangChiTiet: Array<[string, BarEntry[]]> = [
    [t('baoCao.xuatSheetOcr'), tong.ocr.theoLoai],
    [t('baoCao.xuatSheetKho'), tong.kho.theoBo],
    [t('baoCao.xuatSheetBot'), tong.chatbot.theoPhamVi],
    [t('baoCao.xuatSheetHeThong'), tong.heThong.theoPhongBan],
  ];
  for (const [ten, muc] of bangChiTiet) {
    y = bangTheoThanh(doc, ten, muc, y, t);
  }

  // Chân trang: số trang
  const soTrang = doc.getNumberOfPages();
  doc.setFontSize(8);
  for (let i = 1; i <= soTrang; i++) {
    doc.setPage(i);
    doc.text(`${i} / ${soTrang}`, rong - LE_TRAI, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
  }

  return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
}
